import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  createAnalyticsChart as createAnalyticsChartService,
  updateAnalyticsChart as updateAnalyticsChartService,
  deleteAnalyticsChart as deleteAnalyticsChartService, 
  getAnalyticsCharts
} from "../../services/tenant/analytics-chart.service";
import { validate } from "../../utils";
import { handlerErrorV2 } from "../../utils/errors";
import { updateAnalyticsChartsList, updateAnalyticsChartsListMethod } from "../../store/actions/tenant.actions";
import isEmpty from 'lodash/isEmpty';

const useAnalyticsCharts = () => {
  const { enqueueSnackbar } = useSnackbar()

  const dispatch = useDispatch();
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [errors, setErrors] = useState({});

  const fetchAnalyticsCharts = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const response = await getAnalyticsCharts();
      dispatch(updateAnalyticsChartsList(response?.data || []));
    } catch (error) {
      setErrors({ message: error.message || 'An error ocurred'});
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalyticsCharts();
  }, []);

  const validateForm = (form) => {
    const fieldRules = {
      name: 'required',
      link: 'required',
      position: 'required|numeric'
    }

    const errors = validate(fieldRules, form)
    if (!isEmpty(errors)) {
      return { error: true, ...errors }
    }

    return { error: false }
  }

  const createAnalyticsChart = async (form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result
    };

    try {
      const response = await createAnalyticsChartService(form);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateAnalyticsChartsListMethod('POST', response.data));
        enqueueSnackbar('Analytics chart created successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const updateAnalyticsChart = async (analyticsChartId, form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result
    };

    try {
      const response = await updateAnalyticsChartService(analyticsChartId, form);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateAnalyticsChartsListMethod('PUT', response.data));
        enqueueSnackbar('Analytics chart updated successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const deleteAnalyticsChart = async (analyticsChartId) => {
    try {
      const response = await deleteAnalyticsChartService(analyticsChartId);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateAnalyticsChartsListMethod('DELETE', analyticsChartId));
        enqueueSnackbar('Analytics chart deleted successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  return {
    errors,
    isLoadingList,
    createAnalyticsChart,
    updateAnalyticsChart,
    deleteAnalyticsChart
  }
};

export default useAnalyticsCharts;